import {
  getEffectiveNow,
  isOpenEverything
} from '../../../utils/systemTime';
import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchWeeks(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&cq_enabled=eq.true&select=*&order=week_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchSubmissions(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?enrolment_id=eq.${enrolmentId}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchClarificatoryCount(enrolmentId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?enrolment_id=eq.${enrolmentId}&question_type=eq.Clarificatory&select=id`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  return data.length;
}

async function submitCq(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function updateCq(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

function getWindowStatus(week, course, session) {
  if (isOpenEverything(session)) return 'open';

  const match = String(week?.class_date || '')
    .slice(0, 10)
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return 'closed';

  const classDate = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    0, 0, 0, 0
  );

  const now = getEffectiveNow(session);

  // Opens six days before class.
  const windowOpen = new Date(classDate);
  windowOpen.setDate(windowOpen.getDate() - 6);

  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];

  const weekDay = dayNames[classDate.getDay()];

  const resolvedTime =
    course?.class_times?.[weekDay] ||
    course?.class_time ||
    '09:00';

  // Window closes the configured number of hours before class.
  const classStart = new Date(classDate);
  classStart.setHours(
    parseInt(resolvedTime.split(':')[0] || '9'),
    parseInt(resolvedTime.split(':')[1] || '0'),
    0,
    0
  );

  const configuredHours = Number(
    course.cq_due_hours_before_class ?? 1
  );
  const hoursBeforeClass =
    Number.isFinite(configuredHours) && configuredHours >= 0
      ? configuredHours
      : 1;

  const windowClose = new Date(
    classStart.getTime() -
      hoursBeforeClass * 60 * 60 * 1000
  );

  if (now < windowOpen) return 'not_open';
  if (now >= windowClose) return 'closed';

  return 'open';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short'
  });
}

export default function StudentCq({ session, course, enrolment, onBack }) {
  const [weeks, setWeeks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [clarCount, setClarCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [questionType, setQuestionType] = useState('Critical');
  const [questionText, setQuestionText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCriticalInfo, setShowCriticalInfo] = useState(false);
  const [showClarInfo, setShowClarInfo] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchWeeks(course.id),
      fetchSubmissions(enrolment.id),
      fetchClarificatoryCount(enrolment.id)
    ]).then(([w, s, c]) => {
      setWeeks(w);
      setSubmissions(s);
      setClarCount(c);
    }).finally(() => setLoading(false));
  }, [course.id, enrolment.id]);

  function getSubmission(weekId) {
    return submissions.find(s => s.week_id === weekId);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!questionText.trim()) return setError('Please enter your question.');

    const existing = getSubmission(selectedWeek.id);
    const status = getWindowStatus(selectedWeek, course, session);
    if (status !== 'open') return setError('The submission window is not currently open.');

    if (questionType === 'Clarificatory') {
      if (!course.cq_clarificatory_enabled) return setError('Clarificatory questions are not enabled for this course.');
      if (clarCount >= (course.cq_clarificatory_allowed || 0)) {
        return setError(`You have used all your clarificatory questions (${course.cq_clarificatory_allowed} allowed).`);
      }
    }

    setSaving(true);
    try {
      if (existing) {
        const result = await updateCq(existing.id, {
          question_type: questionType,
          question_text: questionText.trim(),
          submitted_at: new Date().toISOString()
        });
        setSubmissions(prev => prev.map(s => s.id === existing.id ? result[0] : s));
      } else {
        const result = await submitCq({
          enrolment_id: enrolment.id,
          week_id: selectedWeek.id,
          question_type: questionType,
          question_text: questionText.trim()
        });
        setSubmissions(prev => [...prev, result[0]]);
        if (questionType === 'Clarificatory') setClarCount(prev => prev + 1);
      }
      setSuccess('Question submitted successfully.');
      setSelectedWeek(null);
      setQuestionText('');
    } catch (err) {
      setError('Failed to submit question. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  function handleSelectWeek(week) {
    const existing = getSubmission(week.id);
    setSelectedWeek(week);
    setQuestionText(existing?.question_text || '');
    setQuestionType(existing?.question_type || 'Critical');
    setError('');
    setSuccess('');
  }

  if (loading) return <div style={styles.wrap}><div style={styles.muted}>Loading...</div></div>;

  if (selectedWeek) {
    const status = getWindowStatus(selectedWeek, course, session);
    const existing = getSubmission(selectedWeek.id);
    const isOpen = status === 'open';

    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          <button style={styles.backLink} onClick={() => { setSelectedWeek(null); setError(''); setSuccess(''); }}>
            ← Back to weeks
          </button>

          <h2 style={styles.heading}>Week {selectedWeek.week_number} — {formatDate(selectedWeek.class_date)}</h2>

          {!isOpen && (
            <div style={styles.closedBanner}>
              {status === 'not_open'
                ? 'The submission window for this week has not opened yet.'
                : 'The submission window for this week has closed.'}
            </div>
          )}

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          {existing && !isOpen && (
            <div style={styles.submittedBox}>
              <div style={styles.submittedLabel}>Your submitted question ({existing.question_type})</div>
              <div style={styles.submittedText}>{existing.question_text}</div>
            </div>
          )}

          {isOpen && (
            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.typeRow}>
                <div style={styles.typeLabel}>Question type:</div>
                <button
                  type="button"
                  style={{ ...styles.typeBtn, ...(questionType === 'Critical' ? styles.typeBtnActive : {}) }}
                  onClick={() => setQuestionType('Critical')}
                >
                  Critical
                </button>
                <button
                  type="button"
                  style={styles.infoBtn}
                  onClick={() => setShowCriticalInfo(prev => !prev)}
                >
                  ?
                </button>
                {course.cq_clarificatory_enabled && (
                  <>
                    <button
                      type="button"
                      style={{ ...styles.typeBtn, ...(questionType === 'Clarificatory' ? styles.typeBtnActive : {}) }}
                      onClick={() => setQuestionType('Clarificatory')}
                    >
                      Clarificatory
                    </button>
                    <button
                      type="button"
                      style={styles.infoBtn}
                      onClick={() => setShowClarInfo(prev => !prev)}
                    >
                      ?
                    </button>
                  </>
                )}
              </div>

              {showCriticalInfo && (
                <div style={styles.infoBox}>
                  <strong>Critical Questions</strong>
                  <p>A critical question engages philosophically with the reading. It identifies something you found puzzling, problematic, or worth pushing back on — and articulates why.</p>
                  <button type="button" style={styles.closeInfoBtn} onClick={() => setShowCriticalInfo(false)}>Close</button>
                </div>
              )}

              {showClarInfo && (
                <div style={styles.infoBox}>
                  <strong>Clarificatory Questions</strong>
                  <p>A clarificatory question asks for clarification about something in the reading you did not understand. You have {course.cq_clarificatory_allowed - clarCount} remaining this semester.</p>
                  <button type="button" style={styles.closeInfoBtn} onClick={() => setShowClarInfo(false)}>Close</button>
                </div>
              )}

              <div style={styles.field}>
                <label style={styles.label}>Your question</label>
                <textarea
                  style={styles.textarea}
                  value={questionText}
                  onChange={e => setQuestionText(e.target.value)}
                  rows={5}
                  placeholder="Enter your question here..."
                />
              </div>

              {existing && (
                <div style={styles.editNote}>
                  You have already submitted a question for this week. Submitting again will replace it.
                </div>
              )}

              <button style={styles.submitBtn} disabled={saving}>
                {saving ? 'Submitting...' : existing ? 'Update Question' : 'Submit Question'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  const submittedCount = submissions.length;
  const enabledCount = weeks.length;

  return (
    <div style={styles.wrap}>
      <div style={styles.content}>
        <h2 style={styles.heading}>Questions Assignment</h2>
        <p style={styles.summary}>
          {submittedCount} of {enabledCount} questions submitted
          {course.cq_clarificatory_enabled && (
            <span style={styles.clarSummary}>
              {' '}· {clarCount} of {course.cq_clarificatory_allowed} clarificatory used
            </span>
          )}
        </p>

        <div style={styles.weekList}>
          {weeks.map(week => {
            const submission = getSubmission(week.id);
            const status = getWindowStatus(week, course, session);

            return (
              <button
                key={week.id}
                style={styles.weekRow}
                onClick={() => handleSelectWeek(week)}
              >
                <div style={styles.weekLeft}>
                  <div style={styles.weekNum}>Week {week.week_number}</div>
                  <div style={styles.weekDate}>{formatDate(week.class_date)}</div>
                </div>
                <div style={styles.weekRight}>
                  {submission ? (
                    <span style={styles.submittedPill}>
                      ✓ {submission.question_type}
                    </span>
                  ) : status === 'open' ? (
                    <span style={styles.openPill}>Open</span>
                  ) : status === 'not_open' ? (
                    <span style={styles.notOpenPill}>Not yet open</span>
                  ) : (
                    <span style={styles.closedPill}>Closed</span>
                  )}
                </div>
                <span style={styles.arrow}>›</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: { minHeight: 0, background: 'transparent' },
  content: { padding: 0, maxWidth: 'none', margin: 0 },
  muted: { color: '#627d98', fontSize: '14px', padding: '20px' },
  heading: { margin: '0 0 8px', fontSize: '26px', color: '#243b53', fontWeight: '700' },
  summary: { fontSize: '14px', color: '#627d98', marginBottom: '20px' },
  clarSummary: { color: '#2f6f9f', fontWeight: '600' },
  backLink: {
    border: 'none', background: 'none', color: '#2f6f9f',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    padding: '0 0 16px', display: 'block'
  },
  weekList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  weekRow: {
    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
    padding: '14px 16px', background: 'white', border: '1px solid #d9e2ec',
    borderRadius: '6px', cursor: 'pointer', textAlign: 'left'
  },
  weekLeft: { flex: 1 },
  weekNum: { fontWeight: '700', fontSize: '14px', color: '#243b53' },
  weekDate: { fontSize: '12px', color: '#627d98', marginTop: '2px' },
  weekRight: { flexShrink: 0 },
  arrow: { color: '#627d98', fontSize: '20px', flexShrink: 0 },
  submittedPill: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  openPill: {
    background: '#eef5fa', color: '#2f6f9f', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  notOpenPill: {
    background: '#edf1f5', color: '#627d98', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  closedPill: {
    background: '#faeaea', color: '#a12a2a', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  closedBanner: {
    background: '#edf1f5', border: '1px solid #d9e2ec', borderRadius: '5px',
    padding: '12px 16px', fontSize: '14px', color: '#627d98', marginBottom: '16px'
  },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '5px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '5px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  submittedBox: {
    background: 'white', border: '1px solid #d9e2ec', borderRadius: '6px',
    padding: '16px', marginBottom: '16px'
  },
  submittedLabel: { fontSize: '12px', fontWeight: '700', color: '#627d98', marginBottom: '8px', textTransform: 'uppercase' },
  submittedText: { fontSize: '15px', color: '#243b53', lineHeight: '1.6' },
  form: {
    background: 'white', border: '1px solid #d9e2ec', borderRadius: '6px', padding: '20px'
  },
  typeRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  typeLabel: { fontSize: '13px', fontWeight: '700', color: '#243b53' },
  typeBtn: {
    border: '1px solid #d9e2ec', background: 'white', borderRadius: '5px',
    padding: '6px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', color: '#627d98'
  },
  typeBtnActive: { background: '#2f6f9f', borderColor: '#2f6f9f', color: 'white' },
  infoBtn: {
    width: '24px', height: '24px', borderRadius: '50%', border: '1px solid #d9e2ec',
    background: '#edf1f5', cursor: 'pointer', fontSize: '12px', fontWeight: '700',
    color: '#627d98', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  infoBox: {
    background: '#f7f8fa', border: '1px solid #d9e2ec', borderRadius: '5px',
    padding: '14px', marginBottom: '14px', fontSize: '14px', color: '#243b53', lineHeight: '1.6'
  },
  closeInfoBtn: {
    border: 'none', background: 'none', color: '#2f6f9f',
    fontSize: '13px', cursor: 'pointer', padding: '4px 0', fontWeight: '600'
  },
  field: { marginBottom: '16px' },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#243b53' },
  textarea: {
    width: '100%', border: '1px solid #d9e2ec', borderRadius: '5px',
    padding: '10px 12px', fontSize: '15px', boxSizing: 'border-box',
    background: 'white', resize: 'vertical', lineHeight: '1.6'
  },
  editNote: {
    fontSize: '13px', color: '#8a5b00', background: '#fff4d7',
    borderRadius: '6px', padding: '8px 12px', marginBottom: '14px'
  },
  submitBtn: {
    width: '100%', background: '#2f6f9f', color: 'white', border: 'none',
    borderRadius: '5px', padding: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
  }
};